"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { useLocale, useTranslations } from "next-intl";
import useRealEstateDetail from "@/hooks/QueryHooks/useRealEstateDetail";
import { useDeferredTransition } from "@/hooks/useDeferredTransition";
import { useDialogDetails, useDialogDetailsModalState } from "../DialogProvider";
import { formatTimeAgo } from "@/lib/time";
import { LocalizationFilters } from "@/constants/filters";
import { locationBreadcrumb } from "../../locationTrail";
export function useDetailDialog() {
  const $ = _c(54);
  const {
    openModal,
    openModalId
  } = useDialogDetailsModalState();
  const t = useTranslations("modules.realEstateDetail");
  const tListingDetail = useTranslations("modules.listings.listingDetail");
  const tPrice = useTranslations("components.price");
  const locale = useLocale();
  const {
    dialogContextItems,
    handleOpenDialog
  } = useDialogDetails();
  let t0;
  if ($[0] !== openModalId) {
    t0 = {
      realEstateSlug: openModalId
    };
    $[0] = openModalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data: detailData,
    isPending,
    isPlaceholderData
  } = useRealEstateDetail(t0);
  const realEstate = detailData?.data?.realEstate;
  const dataMatchesRequest = !!realEstate && (realEstate.slug === openModalId || realEstate.id === openModalId);
  const isLoading = !!openModalId && (isPending || !dataMatchesRequest);
  const t1 = !!openModalId && isPlaceholderData && dataMatchesRequest;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      isTransitioning: t1,
      data: null,
      minVisibleMs: 400
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    isOverlayVisible: isSwitching
  } = useDeferredTransition(t2);
  const analytics = detailData?.data?.analytics;
  const isFavorite = detailData?.data?.isFavorite;
  const favRef = useRef(null);
  const listingTitle = realEstate?.title;
  const businessType = realEstate?.businessType;
  const maxPersons = realEstate?.roomData?.maxPersons;
  const bedrooms = realEstate?.bedrooms;
  const bathrooms = realEstate?.bathrooms;
  let t3;
  if ($[4] !== maxPersons || $[5] !== t) {
    t3 = maxPersons === 1 ? t("singleRoom") : maxPersons === 2 ? t("doubleRoom") : maxPersons && maxPersons > 2 ? t("dormitory") : null;
    $[4] = maxPersons;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const roomTypeLabel = t3;
  const updatedAt = realEstate?.updatedAt;
  let t4;
  if ($[7] !== locale || $[8] !== t || $[9] !== updatedAt) {
    t4 = updatedAt ? t("updatedAgo", {
      time: formatTimeAgo(updatedAt, locale === "pt" ? "pt" : "enGB")
    }) : null;
    $[7] = locale;
    $[8] = t;
    $[9] = updatedAt;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const updatedAgoText = t4;
  let multiLevel;
  let t5;
  if ($[11] !== detailData?.data?.locations) {
    const locations = detailData?.data?.locations ?? [];
    const {
      parts: trailParts,
      multiLevel: t6
    } = locationBreadcrumb(locations);
    multiLevel = t6;
    t5 = trailParts.map(_temp);
    $[11] = detailData?.data?.locations;
    $[12] = multiLevel;
    $[13] = t5;
  } else {
    multiLevel = $[12];
    t5 = $[13];
  }
  const breadcrumbParts = t5;
  const multiLocationsLevel = multiLevel;
  const favoritesCount = analytics?.favoritesCount ?? 0;
  const viewCount = analytics?.viewCount ?? 0;
  const uniqueViewCount = analytics?.uniqueViewCount ?? 0;
  const hasPrice = !!realEstate?.rentPrice || !!realEstate?.salePrice || !!realEstate?.minPrice || !!realEstate?.lastBidPrice;
  let t6;
  if ($[14] !== detailData?.data?.relatedListings || $[15] !== dialogContextItems || $[16] !== realEstate) {
    const relatedListings = detailData?.data?.relatedListings ?? [];
    t6 = dialogContextItems.length ? dialogContextItems : relatedListings.length && realEstate?.slug && realEstate?.title ? [...relatedListings.map(_temp2), {
      slug: realEstate.slug,
      title: realEstate.title
    }].sort(_temp3) : [];
    $[14] = detailData?.data?.relatedListings;
    $[15] = dialogContextItems;
    $[16] = realEstate;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const switcherItems = t6;
  let t7;
  if ($[18] !== openModalId) {
    t7 = item => item.slug === openModalId;
    $[18] = openModalId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const currentContextIndex = switcherItems.findIndex(t7);
  const hasContextSwitcher = switcherItems.length > 1 && currentContextIndex >= 0 && businessType !== "roomRent";
  const houseName = realEstate?.portfolioData?.title ?? null;
  let t8;
  if ($[20] !== handleOpenDialog || $[21] !== switcherItems) {
    t8 = nextIndex => {
      const target = switcherItems[nextIndex];
      if (target) {
        handleOpenDialog(target.slug, switcherItems);
      }
    };
    $[20] = handleOpenDialog;
    $[21] = switcherItems;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleSelectContextIndex = t8;
  const hasFooterBadges = !!roomTypeLabel || (bedrooms ?? 0) > 0 || (bathrooms ?? 0) > 0 || hasPrice || !!analytics || hasContextSwitcher;
  let t9;
  if ($[23] !== tPrice) {
    t9 = tPrice("onRequest");
    $[23] = tPrice;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== analytics || $[26] !== bathrooms || $[27] !== bedrooms || $[28] !== breadcrumbParts || $[29] !== businessType || $[30] !== currentContextIndex || $[31] !== favoritesCount || $[32] !== handleSelectContextIndex || $[33] !== hasContextSwitcher || $[34] !== hasFooterBadges || $[35] !== hasPrice || $[36] !== houseName || $[37] !== isFavorite || $[38] !== isLoading || $[39] !== isSwitching || $[40] !== listingTitle || $[41] !== multiLocationsLevel || $[42] !== openModal || $[43] !== openModalId || $[44] !== realEstate || $[45] !== roomTypeLabel || $[46] !== switcherItems || $[47] !== t || $[48] !== t9 || $[49] !== tListingDetail || $[50] !== uniqueViewCount || $[51] !== updatedAgoText || $[52] !== viewCount) {
    t10 = {
      t,
      tListingDetail,
      openModal,
      openModalId,
      realEstate,
      analytics,
      isFavorite,
      favRef,
      listingTitle,
      businessType,
      roomTypeLabel,
      bedrooms,
      bathrooms,
      favoritesCount,
      viewCount,
      uniqueViewCount,
      hasPrice,
      priceOnRequest: t9,
      hasFooterBadges,
      breadcrumbParts,
      multiLocationsLevel,
      updatedAgoText,
      switcherItems,
      currentContextIndex,
      hasContextSwitcher,
      handleSelectContextIndex,
      houseName,
      isLoading,
      isSwitching
    };
    $[25] = analytics;
    $[26] = bathrooms;
    $[27] = bedrooms;
    $[28] = breadcrumbParts;
    $[29] = businessType;
    $[30] = currentContextIndex;
    $[31] = favoritesCount;
    $[32] = handleSelectContextIndex;
    $[33] = hasContextSwitcher;
    $[34] = hasFooterBadges;
    $[35] = hasPrice;
    $[36] = houseName;
    $[37] = isFavorite;
    $[38] = isLoading;
    $[39] = isSwitching;
    $[40] = listingTitle;
    $[41] = multiLocationsLevel;
    $[42] = openModal;
    $[43] = openModalId;
    $[44] = realEstate;
    $[45] = roomTypeLabel;
    $[46] = switcherItems;
    $[47] = t;
    $[48] = t9;
    $[49] = tListingDetail;
    $[50] = uniqueViewCount;
    $[51] = updatedAgoText;
    $[52] = viewCount;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  return t10;
}
function _temp3(a, b) {
  return a.title.localeCompare(b.title);
}
function _temp2(r) {
  return {
    slug: r.slug,
    title: r.title
  };
}
function _temp(part, index) {
  return {
    label: part.label,
    code: part.value,
    level: index === 0 ? LocalizationFilters.DISTRICT : index === 1 ? LocalizationFilters.COUNTY : LocalizationFilters.PARISH
  };
}